// App shell (I-0a Batch 3; app lock routing I-1a Batch 4; safety surfaces I-1a
// Batch 5). The installable, offline frame: registers the hand-written service
// worker, and routes between S31 First run, S32 Unlock and the placeholder Home
// with the lock state machine (app/lock). On unlock the open EncryptedStore is
// held here; on lock — "Lock now", 5 minutes idle, or > 30 s in the background —
// it is closed (key and decrypted state dropped) and the screens are reset.
//
// Batch 5 (IP2-32 (c)): Hide (S12) is one tap from every screen — it closes the
// store, resets the screens (generation bump) and shows the neutral Unlock
// screen (during first run: the start again, or Unlock if set-up finished).
// Help now (S11) opens over the current screen, before and after unlock; the
// screen underneath stays as it was. The app-switcher cover goes up when the
// app may be leaving. Every screen callback is tied to the generation it was
// rendered in: a late result from a screen that Hide or a lock already closed
// is dropped, and an open store it brings is closed at once.
//
// Batch 6b: after unlock the placeholder Home can open S33 Backup & move (reset
// to Home on every lock and Hide); S31 can restore a backup instead of starting
// empty — it ends in the same `set-up` event. The shell is exactly the viewport
// and only its middle scrolls (footer fix, shell.css).
//
// All copy is neutral English placeholder text (app/screens/copy.ts): the
// product name (D-14) is undecided and must never be "feelings"; Hebrew content
// lives in signed content packages (I-1b), not hard-coded in screens.

import { useCallback, useEffect, useReducer, useRef, useState } from 'react'

import { type EncryptedStore, INITIAL_LOCK_STATE, lockReducer, LockSession, startAutoLock } from '../lock'
import { AppSwitcherCover, HelpNow, useAppSwitcherCover } from '../safety'
import { BackupAndMove } from '../screens/BackupAndMove'
import { FirstRun } from '../screens/FirstRun'
import { HomePlaceholder } from '../screens/HomePlaceholder'
import { unavailable } from '../screens/copy'
import { Unlock } from '../screens/Unlock'
import { TopBar } from '../ui/TopBar'
import { currentBuildLabel } from '../update/buildInfo'
import { registerServiceWorker } from '../update/registerServiceWorker'
import { isUpdateWaiting } from '../update/updateState'

import '../screens/screens.css'
import './shell.css'

export function AppShell(): React.JSX.Element {
  const [updateReady, setUpdateReady] = useState(false)
  const [session] = useState(() => new LockSession())
  const [state, dispatch] = useReducer(lockReducer, INITIAL_LOCK_STATE)
  const [storageProblem, setStorageProblem] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  // After unlock: the placeholder Home, or S33 Backup & move (Batch 6b).
  const [backupOpen, setBackupOpen] = useState(false)
  const uncover = useAppSwitcherCover()
  // Bumped on every lock and Hide, so no screen keeps state from before. The ref
  // moves at once (inside the handler); the state re-renders the screens.
  const [generation, setGeneration] = useState(0)
  const generationRef = useRef(0)
  const store = useRef<EncryptedStore | null>(null)

  useEffect(() => {
    // Register the service worker and track only whether a new version is
    // waiting, so the shell can show a quiet, non-blocking line (Add9 F7). In
    // dev this returns null and nothing is registered.
    void registerServiceWorker({
      onPhaseChange: (phase) => {
        setUpdateReady(isUpdateWaiting(phase))
      },
    })
  }, [])

  // Read the keyring whenever the machine is checking: at start, after Hide
  // during first run, and after a recheck.
  const checking = state.name === 'checking'
  useEffect(() => {
    if (!checking) {
      return undefined
    }
    let live = true
    session
      .status()
      .then((status) => {
        if (live) {
          dispatch({ type: 'checked', hasKeyring: status.hasKeyring })
        }
      })
      .catch(() => {
        if (live) {
          setStorageProblem(true)
        }
      })
    return () => {
      live = false
    }
  }, [session, checking])

  const reset = useCallback((event: 'lock' | 'hide') => {
    store.current?.close()
    store.current = null
    generationRef.current += 1
    setGeneration(generationRef.current)
    setBackupOpen(false)
    dispatch({ type: event })
  }, [])

  const lock = useCallback(() => reset('lock'), [reset])
  const hide = useCallback(() => {
    setHelpOpen(false)
    reset('hide')
  }, [reset])

  const unlocked = state.name === 'unlocked'
  useEffect(() => (unlocked ? startAutoLock(lock) : undefined), [unlocked, lock])

  // Callbacks below belong to the screens of this render's generation.
  const rendered = generation
  const current = (): boolean => generationRef.current === rendered

  let screen: React.JSX.Element | null = null
  if (storageProblem) {
    screen = <p className="screen__text">{unavailable.text}</p>
  } else {
    switch (state.name) {
      case 'checking':
        screen = null
        break
      case 'first-run':
        screen = (
          <FirstRun
            session={session}
            onOpened={(opened) => {
              if (!current()) {
                opened.close()
                dispatch({ type: 'recheck' })
                return
              }
              store.current = opened
              dispatch({ type: 'set-up' })
            }}
            onAlreadySetUp={() => dispatch({ type: current() ? 'already-set-up' : 'recheck' })}
          />
        )
        break
      case 'locked':
      case 'unlocking':
        screen = (
          <Unlock
            session={session}
            onTry={() => {
              if (current()) {
                dispatch({ type: 'try' })
              }
            }}
            onFailed={() => {
              if (current()) {
                dispatch({ type: 'failed' })
              }
            }}
            onOpened={(opened, failedTries) => {
              if (!current()) {
                opened.close()
                return
              }
              store.current = opened
              dispatch({ type: 'opened', failedTries })
            }}
            onDeleted={() => dispatch({ type: current() ? 'deleted' : 'recheck' })}
          />
        )
        break
      case 'unlocked':
        screen =
          backupOpen && store.current ? (
            <BackupAndMove session={session} store={store.current} onBack={() => setBackupOpen(false)} />
          ) : (
            <HomePlaceholder
              failedTries={state.failedTries}
              readOnlyReason={store.current?.readOnlyReason ?? null}
              storageFull={store.current?.isFull ?? false}
              onBackup={() => setBackupOpen(true)}
              onLock={lock}
            />
          )
        break
    }
  }

  return (
    <div className="shell">
      <TopBar onHide={hide} onHelpNow={() => setHelpOpen(true)} />
      <main className="shell__main" key={generation}>
        {/* The screen stays mounted under Help now, so nothing typed is lost. */}
        <div className="shell__screen" hidden={helpOpen}>
          {screen}
        </div>
        {helpOpen ? <HelpNow onBack={() => setHelpOpen(false)} /> : null}
        {updateReady ? (
          <p className="shell__update" role="status">
            A newer version is ready. It will apply the next time you reopen the app.
          </p>
        ) : null}
      </main>
      <footer className="shell__safety">
        {/* Two independent siblings. The devlink is PILOT-ONLY (I-0a Batch 6):
            the link and the device test page are removed before I-1b. The build
            label is PERMANENT and the same on every channel (production too); it
            stays, right-aligned, when the devlink goes. Today it shows commit +
            date; in I-1a Batch 8 (signed release.json) it switches to the signed
            release version. Text node only. */}
        <a className="shell__devlink" href="/device-test">
          Device test (pilot)
        </a>
        <span className="shell__build">{currentBuildLabel()}</span>
      </footer>
      <AppSwitcherCover onTap={uncover} />
    </div>
  )
}
